import {
  ChangeDetectionStrategy, Component, OnInit, AfterViewInit, OnDestroy,
  ViewChild, ChangeDetectorRef, inject, signal, ElementRef
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Observable, debounceTime, distinctUntilChanged, filter, finalize } from 'rxjs';

export interface LookupSelectOption {
  value: string | number;
  label: string;
  subLabel?: string;
  searchText: string; // pre-lowercased text used for filtering
}

export interface LookupSelectDialogData {
  title: string;
  options: LookupSelectOption[];
  selectedValue?: string | number | null;
  /** Pre-built option for the selected value in server-side mode; pinned to top on initial load. */
  selectedOption?: LookupSelectOption;
  isLoading?: boolean;
  /** When present, activates server-side search + infinite scroll (WorkOrder mode). */
  searchFn?: (search: string, page: number) => Observable<LookupSelectOption[]>;
}

@Component({
  selector: 'app-lookup-select-dialog',
  templateUrl: './lookup-select-dialog.component.html',
  styleUrl: './lookup-select-dialog.component.scss',
  // eslint-disable-next-line @angular-eslint/prefer-standalone -- declared in SharedModule, not yet migrated to standalone
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LookupSelectDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  data: LookupSelectDialogData = inject(MAT_DIALOG_DATA);
  private dialogRef = inject<MatDialogRef<LookupSelectDialogComponent>>(MatDialogRef);
  private cdr = inject(ChangeDetectorRef);

  @ViewChild(CdkVirtualScrollViewport) viewport!: CdkVirtualScrollViewport;
  @ViewChild('scrollSentinel') scrollSentinel?: ElementRef<HTMLElement>;

  searchControl = new FormControl('');

  allOptions: LookupSelectOption[] = [];
  filteredOptions: LookupSelectOption[] = [];
  selectedValue: string | number | null = null;
  title = 'Select';
  isLoading = false;

  // Server-side mode (WorkOrder) signals
  isInitialLoading = signal(false);
  isFetchingMore = signal(false);
  hasMore = signal(true);

  private readonly PAGE_SIZE = 50;
  private currentPage = 1;
  private currentSearchTerm = '';
  private intersectionObserver?: IntersectionObserver;
  private observerSetup = false;

  constructor() {
    this.allOptions = this.data?.options ?? [];
    this.selectedValue = this.data?.selectedValue ?? null;
    this.title = this.data?.title || 'Select';
    this.isLoading = this.data?.isLoading ?? false;
    this.moveSelectedToTop();
    this.filteredOptions = [...this.allOptions];
  }

  private moveSelectedToTop(): void {
    if (this.selectedValue == null || this.selectedValue === '') return;
    const idx = this.allOptions.findIndex(o => o.value == this.selectedValue);
    if (idx > 0) {
      this.allOptions = [
        this.allOptions[idx],
        ...this.allOptions.slice(0, idx),
        ...this.allOptions.slice(idx + 1)
      ];
    }
  }

  ngOnInit(): void {
    if (this.data.searchFn) {
      this.initServerSideSearch();
      this.isInitialLoading.set(true);
      this.fetchPage('', 1, 'replace');
    } else {
      this.searchControl.valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged()
      ).subscribe(search => this.filterList(search || ''));
    }
  }

  private initServerSideSearch(): void {
    this.searchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      filter(term => (term?.length ?? 0) === 0 || (term?.length ?? 0) >= 2)
    ).subscribe(term => {
      this.currentSearchTerm = term ?? '';
      this.currentPage = 1;
      this.hasMore.set(true);
      this.isInitialLoading.set(true);
      this.filteredOptions = [];
      this.cdr.markForCheck();
      this.fetchPage(this.currentSearchTerm, 1, 'replace');
    });
  }

  private fetchPage(term: string, page: number, mode: 'replace' | 'append'): void {
    this.data.searchFn!(term, page).pipe(
      finalize(() => {
        this.isFetchingMore.set(false);
        this.isInitialLoading.set(false);
        this.cdr.markForCheck();
        if (!this.observerSetup) {
          this.observerSetup = true;
          setTimeout(() => this.setupIntersectionObserver(), 50);
        }
      })
    ).subscribe({
      next: items => {
        // Discard stale responses that arrived after the search term changed.
        if (term !== this.currentSearchTerm) return;
        if (mode === 'append') this.currentPage = page;
        let list = mode === 'replace' ? items : [...this.filteredOptions, ...items];
        // On the initial empty-search page, pin the pre-selected option at the top
        // if it didn't come back in the first page of results.
        if (mode === 'replace' && term === '' && this.data.selectedOption && this.selectedValue != null) {
          const alreadyPresent = list.some(o => o.value == this.selectedValue);
          if (!alreadyPresent) {
            list = [this.data.selectedOption, ...list];
          }
        }
        this.filteredOptions = list;
        this.hasMore.set(items.length >= this.PAGE_SIZE);
        this.cdr.markForCheck();
      },
      error: () => {
        if (term !== this.currentSearchTerm) return;
        this.hasMore.set(false);
      }
    });
  }

  private fetchNextPage(): void {
    if (this.isFetchingMore() || !this.hasMore()) return;
    this.isFetchingMore.set(true);
    // Pass the candidate page number; currentPage is only committed on success
    // inside fetchPage to avoid leaving it in a wrong state on error.
    this.fetchPage(this.currentSearchTerm, this.currentPage + 1, 'append');
  }

  private setupIntersectionObserver(): void {
    if (!this.scrollSentinel?.nativeElement) return;
    this.intersectionObserver = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && this.hasMore() && !this.isFetchingMore()) {
          this.fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );
    this.intersectionObserver.observe(this.scrollSentinel.nativeElement);
  }

  ngAfterViewInit(): void {
    if (!this.data.searchFn) {
      this.refreshViewport();
    }
  }

  ngOnDestroy(): void {
    this.intersectionObserver?.disconnect();
  }

  private refreshViewport(): void {
    setTimeout(() => {
      if (this.viewport) {
        this.viewport.checkViewportSize();
        this.cdr.detectChanges();
      }
      setTimeout(() => {
        if (this.viewport) {
          this.viewport.checkViewportSize();
          this.cdr.detectChanges();
        }
      }, 100);
    }, 50);
  }

  filterList(search: string): void {
    const term = search.toLowerCase().trim();
    this.filteredOptions = !term
      ? [...this.allOptions]
      : this.allOptions.filter(o => o.searchText.includes(term));
    this.refreshViewport();
    this.cdr.markForCheck();
  }

  trackByValue(index: number, item: LookupSelectOption): string | number {
    return item.value ?? index;
  }

  isSelected(option: LookupSelectOption): boolean {
    return this.selectedValue != null && this.selectedValue !== '' && option.value == this.selectedValue;
  }

  selectOption(option: LookupSelectOption): void {
    this.dialogRef.close(option);
  }

  close(): void {
    this.dialogRef.close(null);
  }
}
